import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Loader2, CheckCircle2, XCircle } from "lucide-react";

const SECTION_TITLES = {
  service: "Selected Service", testatorA: "First Testator (A)", testatorB: "Spouse / Partner (B)",
  contact: "Contact Details", willHistory: "Will History", assets: "Assets & Estate",
  children: "Children", childrenList: "Children", guardians: "Guardians", executors: "Executors",
  executorList: "Appointed Executors", funeral: "Funeral Requests", residual: "Residual Estate",
  beneficiaries: "Beneficiaries", reserveBeneficiaries: "Reserve Beneficiaries",
  legacies: "Legacies / Bequests", exclusions: "Exclusions",
};

const LABELS = {
  title: "Title", firstNames: "First names", surname: "Surname", dob: "Date of Birth",
  idVerified: "ID Verified", divSepWid: "Divorced/Separated/Widowed", maritalStatus: "Marital status",
  assetsOtherName: "Assets under other name", contemplationMarriage: "In contemplation of marriage/CP",
  contemplationDivorce: "In contemplation of divorce", address: "Address", city: "City/Town",
  county: "County", postcode: "Postcode", homeTel: "Home tel", daytimeTel: "Daytime tel",
  mobileTel: "Mobile tel", emailA: "Email (A)", emailB: "Email (B)", madeWillA: "(A) Made a will before",
  madeWillB: "(B) Made a will before", changesA: "(A) Changes", changesB: "(B) Changes",
  medicalExpress: "Medical reason for express will", readingSigningA: "(A) Reading/signing needs",
  readingSigningB: "(B) Reading/signing needs", name: "Name", share: "Share", ageVesting: "Age of vesting",
  relationshipA: "Relationship to (A)", relationshipB: "Relationship to (B)", giftOverAge: "Gift over age",
  tel: "Tel", relationship: "Relationship", reserve: "Joint reserve", recipient: "Recipient",
  charityNo: "Charity No.", description: "Description or amount", vestingAge: "Vesting age",
  secondDeath: "Applies on", fullName: "Full name", ageDob: "Age / D.O.B", gender: "M/F",
  whoseChild: "Whose child", whoHasPR: "Who has P.R.", package: "Package", optionA: "(A) Executor arrangement",
  optionB: "(B) Executor arrangement", toSpouse: "Residue firstly to spouse/partner",
  thenChildren: "Then to children equally", includeUnborn: "Include unborn children",
  toNamed: "To named beneficiaries", excluded: "Deliberately excluded anyone", mutuallyAgreed: "Mutually agreed",
  letterAdvised: "Advised to leave letter", people: "People who may claim", prePaid: "Pre-paid funeral plan",
  providerPlan: "Provider & plan", moreInfo: "Wants more info", aType: "Testator A", bType: "Testator B",
  foreignOwned: "Foreign property owned", foreignWill: "Foreign will exists", brusselsElection: "Brussels IV election",
  marriedBeforeA: "(A) Married before", marriedBeforeB: "(B) Married before", self: "Self", spouse: "Spouse", joint: "Joint",
};

const lbl = (k) => LABELS[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, " $1") : k);

const fmt = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
};

const isScalar = (v) => v === null || typeof v !== "object";

function KV({ obj }) {
  const entries = Object.entries(obj).filter(([, v]) => isScalar(v) || (Array.isArray(v) && v.every(isScalar)));
  if (!entries.length) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 py-1 border-b border-brand-line/60">
          <dt className="text-brand-muted text-sm">{lbl(k)}</dt>
          <dd className="text-brand-ink text-sm font-medium text-right">{fmt(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function renderSection(key, value) {
  const title = SECTION_TITLES[key] || lbl(key);
  const blocks = [];
  if (Array.isArray(value)) {
    if (!value.length) return null;
    blocks.push(
      <div key="arr" className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="rounded-lg border border-brand-line bg-[#FCFBFA] p-3">
            <div className="text-xs font-semibold text-brand-gold mb-1">Entry {i + 1}</div>
            {isScalar(item) ? <div className="text-sm text-brand-ink">{fmt(item)}</div> : <KV obj={item} />}
          </div>
        ))}
      </div>
    );
  } else if (value && typeof value === "object") {
    blocks.push(<KV key="kv" obj={value} />);
    Object.entries(value).forEach(([k, v]) => {
      if (v && typeof v === "object" && !(Array.isArray(v) && v.every(isScalar))) {
        blocks.push(
          <div key={k} className="mt-2">
            <div className="text-xs font-semibold text-brand-muted mb-1">{lbl(k)}</div>
            {Array.isArray(v)
              ? v.map((it, i) => <div key={i} className="rounded-lg border border-brand-line bg-[#FCFBFA] p-3 mb-2"><KV obj={it} /></div>)
              : <KV obj={v} />}
          </div>
        );
      }
    });
  }
  if (!blocks.length) return null;
  return (
    <section key={key} className="mb-5">
      <h3 className="font-serif text-lg text-brand-green mb-2 pb-1 border-b border-brand-gold/40">{title}</h3>
      {blocks}
    </section>
  );
}

const ORDER = ["service", "testatorA", "testatorB", "contact", "willHistory", "assets",
  "children", "childrenList", "guardians", "executors", "executorList", "funeral",
  "residual", "beneficiaries", "reserveBeneficiaries", "legacies", "exclusions"];

export function ClientDetailDialog({ open, onOpenChange, detail, loading, onDownload, downloading }) {
  const data = detail?.will?.data || {};
  const p = detail?.progress || {};

  const downloadDoc = async (docType) => {
    if (!detail?.client) return;
    try {
      const res = await api.get(`/admin/clients/${detail.client.id}/documents/${docType}/download`, { responseType: "blob" });
      const doc = detail.documents?.[docType];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc?.filename || docType; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download document"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white" data-testid="client-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-green">{detail?.client?.name || "Client"}</DialogTitle>
          <p className="text-sm text-brand-muted">{detail?.client?.email}</p>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="outline" className={p.form_completed ? "border-brand-success text-brand-success" : "border-brand-line text-brand-muted"}>
                {p.form_completed ? "Form completed" : p.form_started ? "Form in progress" : "Not started"}
              </Badge>
              <Badge variant="outline" className={p.payment_made ? "border-brand-green text-brand-green" : "border-brand-line text-brand-muted"}>
                {p.payment_made ? `Paid${p.package_label ? " · " + p.package_label : ""}` : "Unpaid"}
              </Badge>
              {p.submitted && <Badge className="bg-brand-green text-white">Submitted</Badge>}
            </div>

            {(() => {
              const keys = Object.keys(data);
              if (!keys.length) {
                return <p className="text-brand-muted text-sm py-6 text-center">This client has not entered any form details yet.</p>;
              }
              const known = ORDER.filter((k) => data[k] !== undefined && data[k] !== null && data[k] !== "");
              const extraObjectKeys = keys.filter((k) => !ORDER.includes(k) && k !== "hasPartner" && !isScalar(data[k]));
              const extraScalar = keys.filter((k) => !ORDER.includes(k) && k !== "hasPartner" && isScalar(data[k]) && data[k] !== "" && data[k] !== null);
              return (
                <>
                  {known.map((k) => renderSection(k, k === "testatorB" && !data.hasPartner ? null : data[k]))}
                  {extraObjectKeys.map((k) => renderSection(k, data[k]))}
                  {extraScalar.length > 0 && (
                    <section className="mb-5">
                      <h3 className="font-serif text-lg text-brand-green mb-2 pb-1 border-b border-brand-gold/40">Other details</h3>
                      <KV obj={Object.fromEntries(extraScalar.map((k) => [k, data[k]]))} />
                    </section>
                  )}
                </>
              );
            })()}

            {detail?.document_labels && (
              <section className="mb-5">
                <h3 className="font-serif text-lg text-brand-green mb-2 pb-1 border-b border-brand-gold/40">Verification documents</h3>
                <div className="space-y-2">
                  {Object.keys(detail.document_labels).map((dt) => {
                    const doc = detail.documents?.[dt];
                    return (
                      <div key={dt} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-2 text-brand-ink">
                          {doc ? <CheckCircle2 className="w-4 h-4 text-brand-success" /> : <XCircle className="w-4 h-4 text-brand-muted" />}
                          {detail.document_labels[dt]}
                        </span>
                        {doc ? (
                          <button onClick={() => downloadDoc(dt)} className="text-brand-green underline text-sm flex items-center gap-1" data-testid={`detail-doc-${dt}`}>
                            <Download className="w-3.5 h-3.5" /> {doc.filename}
                          </button>
                        ) : <span className="text-brand-muted">Not uploaded</span>}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {detail?.will?.data && Object.keys(detail.will.data).length > 0 && (
              <Button onClick={onDownload} disabled={downloading} className="mt-2 bg-brand-green hover:bg-brand-greenHover text-white" data-testid="detail-download-pdf">
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download PDF
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
